import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMessageSquare, FiX, FiSend } from "react-icons/fi";

const QNA = {
  "Hire Talent": "We specialize in IT & Non-IT recruitment across India. Our team can help you find specialized talent within 4-48 hours. Please share your requirements at info@minivel.com.",
  "Find a Job": "We have active openings in IT, Manufacturing, and Healthcare. Please share your resume with us at careers@minivel.com for immediate matching.",
  "Contract Staffing": "Minivel offers end-to-end contract staffing and compliance support. Let us know your requirements and we'll get back to you with a proposal.",
  "Talk to Human": "Our advisors are available Mon-Fri, 9:30 AM to 6:30 PM. Please call +91 9175111350."
};

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: "bot", text: "Hello! 👋 Welcome to Minivel support. How can I help you today?", time: new Date() }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen, isTyping]);

  const handleSend = (text = inputValue) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { type: "user", text: text.trim(), time: new Date() }]);
    setInputValue("");
    
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      let botResponse = "Thank you for reaching out. A Minivel advisor will be with you shortly. You can also email us at info@minivel.com.";
      if (QNA[text]) botResponse = QNA[text];
      setMessages(prev => [...prev, { type: "bot", text: botResponse, time: new Date() }]);
    }, 1000);
  };

  return (
    <div className="z-[1001]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Floating Trigger Button - Professional & Simple */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-14 h-14 bg-[#70be44] rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 active:scale-95 transition-all z-[1001]"
          >
            <FiMessageSquare className="text-white text-2xl" />
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-sky-500 border-2 border-white"></span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modern Compact Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            className="fixed bottom-6 right-6 w-[320px] sm:w-[340px] h-[480px] bg-white rounded-3xl shadow-2xl flex flex-col z-[1002] overflow-hidden border border-gray-100"
          >
            {/* Clean Header */}
            <div className="bg-[#1e293b] p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#70be44] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                   M
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm leading-tight">Minivel Assistant</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 bg-[#70be44] rounded-full"></span>
                    <span className="text-[10px] text-gray-400 font-medium">Online</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
                title="Close"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-gray-50/50">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                    msg.type === "user" 
                      ? "bg-[#70be44] text-white rounded-tr-none" 
                      : "bg-white text-slate-700 rounded-tl-none border border-gray-100"
                  }`}>
                    {msg.text}
                    <div className={`text-[9px] mt-1.5 opacity-50 ${msg.type === "user" ? "text-right" : "text-left"}`}>
                      {msg.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl px-4 py-2 border border-gray-100 flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce"></span>
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                  </div>
                </div>
              )}
              
              {/* Quick Actions */}
              {!isTyping && messages[messages.length - 1].type === "bot" && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.keys(QNA).map((key) => (
                    <button
                      key={key}
                      onClick={() => handleSend(key)}
                      className="text-[11px] font-medium bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-full hover:bg-[#70be44] hover:text-white hover:border-[#70be44] transition-all active:scale-95 shadow-sm"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Bar */}
            <div className="p-4 bg-white border-t border-gray-100">
              <div className="flex items-center gap-2 bg-gray-100/50 rounded-full p-1 pl-4">
                <input 
                  type="text"
                  placeholder="Ask a question..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none text-gray-700"
                />
                <button 
                  onClick={() => handleSend()}
                  disabled={!inputValue.trim()}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    inputValue.trim() 
                      ? "bg-[#70be44] text-white shadow-md hover:scale-105" 
                      : "bg-gray-200 text-gray-400"
                  }`}
                >
                  <FiSend size={16} className={inputValue.trim() ? "translate-x-0.5" : ""} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
